import { inject } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import type { CheckoutData } from '~~/common/stores/checkout';
import type { DeepPartial, Schema } from '~~/common/types/api';
import type { CartSplit } from '~~/common/stores/order';
import type { Navigation } from '~~/common/config/Navigation';

/**
 * Types of the values `shop/checkout/[type].vue` provides to its blocks. The keys stay plain strings
 * so the not-yet-migrated blocks keep injecting them unchanged; a block that is migrated passes the
 * matching type here as `inject<CurrentCheckout>('currentCheckout')`. Being strings, these types are
 * asserted on the consumer side, not checked against the `provide()` call.
 */
export type CurrentCheckout = ComputedRef<CheckoutData>;
export type CurrentCheckoutType = ComputedRef<string>;
export type CurrentCartSplit = ComputedRef<CartSplit | null>;
export type PackageCount = ComputedRef<number>;
export type CanStartOrderSubmit = Ref<boolean>;

/** One row of the „why can't the order be submitted" list built by `checkCanStartOrderSubmit`. */
export type OrderSubmitCheck = {
  isValid: boolean;
  message: string;
};
export type CanStartOrderSubmitMessage = Ref<OrderSubmitCheck[] | null>;

// The page keeps these in refs initialised with `?? null`, so the setters accept null as well as the
// schema's own `undefined`.
export type UpdateDeliveryData = (deliveryData: CheckoutData['delivery'] | null) => void;
export type UpdateBillingData = (billingData: CheckoutData['billing'] | null) => void;
export type SetIsEditingFormData = (value: boolean) => void;
export type IsEditingFormData = Ref<boolean>;
export type UpdateLoyaltyPointInputInFocus = (focus: boolean) => void;
export type JumpFocus = (id: NavigationId) => void;
export type StartOrderButtonText = ComputedRef<string>;

/**
 * Keys of `Navigation` that map to a plain element id. `ID_BASKET_SPLIT` maps to a function instead
 * and cannot be looked up with `getElementById`, so it is excluded.
 */
export type NavigationId = {
  [K in keyof typeof Navigation]: (typeof Navigation)[K] extends string ? K : never;
}[keyof typeof Navigation];

/**
 * The agreeable statements form as `AwCheckoutAgreeableStatementsForm` hands it back: one row per
 * statement, keyed by its id. Only the checkbox value is read by the consumers.
 */
export type AgreementForm = Record<string, { model?: boolean }>;

/**
 * One selectable pickup point on the map. The API's own model plus the four fields
 * `AwCheckoutMapBlock` attaches while collecting the points of every delivery method.
 */
export type PickupPoint = DeepPartial<Schema<'PickupPointModel'>> & {
  type?: string;
  price?: DeepPartial<Schema<'PriceModel'>>;
  nonFoodCourierId?: number;
  promotional?: boolean;
};

/**
 * Injects one of the values above. A missing provide is a wiring mistake rather than a case to
 * handle, so this throws instead of handing back an `undefined` that every call site would have to
 * guard.
 *
 * Not every consumer renders under the checkout page: the pickup-or-locker pair is also reached from
 * the delivery-method modal, which re-provides `currentCheckout`, `currentCheckoutType` and
 * `updateDeliveryData` (`AwPickupOrLockerForm.vue`). An inject added to a block on that path has to
 * be re-provided there too.
 */
export function injectCheckout<T> (key: string): T {
  const value = inject<T>(key);
  if (value === undefined) {
    throw new Error(`Missing provide for "${key}": this component must render inside the checkout page.`);
  }
  return value;
}
